import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import { Typography, Grid, Card as MuiCard, CardContent, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getGuidesByClass } from '../TourPage/GuidePage/GuideApi';
import { CharacterClass } from '../TourPage/GuidePage/GuideApi';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import Card from 'components/Card/Card.js';
import CardHeader from 'components/Card/CardHeader.js';
import CardBody from 'components/Card/CardBody.js';
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import NavPills from 'components/NavPills/NavPills.js';
import styles from 'assets/jss/material-kit-react/views/profilePage.js';
import { useDynamicPickitPacks } from '../../hooks/useDynamicPickitPacks';
import { useNavigate } from 'react-router-dom';
import BubbleContainer from 'components/BubbleContainer/BubbleContainer';
import config from '../../config/config.json';

// Import class images
import amazonIcon from 'assets/img/amazonicon.png';
import amazonLarge from 'assets/img/amazon_full.png';
import assassinIcon from 'assets/img/assassinicon.png';
import assassinLarge from 'assets/img/assassin_full.png';
import barbarianIcon from 'assets/img/barbarianicon.png';
import barbarianLarge from 'assets/img/barbarian_full.png';
import druidIcon from 'assets/img/druidicon.png';
import druidLarge from 'assets/img/druid_full.png';
import necromancerIcon from 'assets/img/necromancericon.png';
import necromancerLarge from 'assets/img/necromancer_full.png';
import paladinIcon from 'assets/img/paladinicon.png';
import paladinLarge from 'assets/img/paladin_full.png';
import sorceressIcon from 'assets/img/sorceressicon.png';
import sorceressLarge from 'assets/img/sorceress_full.png';

const classImages = {
  amazon: {
    icon: amazonIcon,
    large: amazonLarge,
  },
  assassin: {
    icon: assassinIcon,
    large: assassinLarge,
  },
  barbarian: {
    icon: barbarianIcon,
    large: barbarianLarge,
  },
  druid: {
    icon: druidIcon,
    large: druidLarge,
  },
  necromancer: {
    icon: necromancerIcon,
    large: necromancerLarge,
  },
  paladin: {
    icon: paladinIcon,
    large: paladinLarge,
  },
  sorceress: {
    icon: sorceressIcon,
    large: sorceressLarge,
  },
} as const;

const useStyles = makeStyles<Theme>((theme) => ({
  mainContainer: {
    position: 'relative',
    zIndex: 2,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
  },
  container: {
    ...styles.container,
    position: 'relative',
    zIndex: 3,
  },
  section: {
    marginBottom: '20px',
  },
  classImage: {
    width: '120px',
    height: '120px',
    borderRadius: '8px',
    marginBottom: '20px',
    objectFit: 'cover',
  },
  classImageLarge: {
    width: '100%',
    maxWidth: '600px',
    height: 'auto',
    borderRadius: '8px',
    marginTop: '20px',
    objectFit: 'cover',
  },
  classInfo: {
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
  },
  classHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
    textAlign: 'center',
    margin: 0,
  },
  className: {
    fontSize: '24px',
    fontWeight: 'bold',
    margin: '10px 0',
  },
  centeredContent: {
    textAlign: 'center',
  },
  centeredHeader: {
    textAlign: 'center',
    margin: 0,
  },
  guideCard: {
    padding: '15px',
    borderRadius: '6px',
    marginTop: '10px',
    marginBottom: '10px',
  },
  packCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-4px)',
    },
  },
  packImage: {
    width: '100%',
    height: 200,
    objectFit: 'cover',
  },
  packTitle: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
  },
  packDescription: {
    marginBottom: theme.spacing(2),
    flexGrow: 1,
  },
  packPrice: {
    fontWeight: 'bold',
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(2),
  },
  noPacksMessage: {
    textAlign: 'center',
    width: '100%',
    padding: theme.spacing(4),
  },
}));

export default function ClassDetailPage() {
  const { t } = useTranslation();
  const classes = useStyles();
  const { className } = useParams<{ className: string }>();
  const navigate = useNavigate();

  useDocumentTitle(t(`class.${className}.name`));

  const { data: guides, isLoading: isLoadingGuides } = useQuery({
    queryKey: ['guides', className],
    queryFn: () => getGuidesByClass(className as CharacterClass),
    enabled: !!className,
  });

  const { packs, isLoading: isLoadingPacks } = useDynamicPickitPacks();

  const classSpecificPacks = (packs || []).filter((pack) =>
    pack.tags.some((tag) => tag.toLowerCase() === className?.toLowerCase())
  );

  const handleViewPack = (packId: number | undefined) => {
    if (typeof packId === 'number') {
      navigate(`/pack/${packId}`);
    }
  };

  const tabs = [
    {
      tabButton: t('class.details.label'),
      tabName: 'details',
      tabContent: (
        <div className={classes.section}>
          <Typography variant="h5" gutterBottom>
            {t('class.details.title')}
          </Typography>
          {/* Add class details content here */}
        </div>
      ),
    },
    {
      tabButton: t('class.guides.label'),
      tabName: 'guides',
      tabContent: (
        <div className={classes.section}>
          <Typography variant="h5" gutterBottom>
            {t('class.guides.title')}
          </Typography>
          {isLoadingGuides ? (
            <Typography>{t('common.loading.fetchingData')}</Typography>
          ) : (
            guides?.map((guide) => (
              <div key={guide.id} className={classes.guideCard}>
                <Typography variant="h6">{guide.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {guide.description}
                </Typography>
              </div>
            ))
          )}
        </div>
      ),
    },
    {
      tabButton: t('class.packs.label'),
      tabName: 'packs',
      tabContent: (
        <div className={classes.section}>
          <Typography variant="h5" gutterBottom>
            {t('class.packs.title')}
          </Typography>
          {isLoadingPacks ? (
            <Typography>{t('common.loading.fetchingData')}</Typography>
          ) : classSpecificPacks.length === 0 ? (
            <Typography variant="h6" className={classes.noPacksMessage}>
              {t('class.packs.none')}
            </Typography>
          ) : (
            <Grid container spacing={3}>
              {classSpecificPacks.map((pack) => (
                <Grid item xs={12} sm={6} md={4} key={pack.id}>
                  <MuiCard
                    className={classes.packCard}
                    onClick={() => handleViewPack(pack.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    {pack.image_path && (
                      <img
                        src={`${config.backend_base_url}image/${pack.image_path}`}
                        alt={pack.title}
                        className={classes.packImage}
                      />
                    )}
                    <CardContent>
                      <Typography variant="h6" className={classes.packTitle}>
                        {pack.title}
                      </Typography>
                      <Typography variant="h6" className={classes.packPrice}>
                        ${pack.price.toFixed(2)}
                      </Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={(e) => {
                          e.stopPropagation();
                          // Add buy functionality here
                        }}
                      >
                        {t('class.packs.buy')}
                      </Button>
                    </CardContent>
                  </MuiCard>
                </Grid>
              ))}
            </Grid>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className={classes.mainContainer}>
      <BubbleContainer pageKey="classDetail" helpText={t('help.classDetail')} />
      <div className={classes.container}>
        <Card>
          <CardHeader color="primary">
            <div className={classes.classHeader}>
              <img
                src={classImages[className as keyof typeof classImages].icon}
                alt={t(`class.${className}.name`)}
                className={classes.classImage}
                style={{ marginBottom: 0, width: 48, height: 48 }}
              />
              <h4 className={classes.centeredHeader} style={{ margin: 0 }}>
                {t(`class.${className}.name`)}
              </h4>
            </div>
          </CardHeader>
          <CardBody>
            <GridContainer>
              <GridItem xs={12}>
                <div className={classes.classInfo}>
                  <Typography variant="h4" className={classes.className}>
                    {t(`class.${className}.name`)}
                  </Typography>
                  <img
                    src={classImages[className as keyof typeof classImages].large}
                    alt={t(`class.${className}.name`)}
                    className={classes.classImageLarge}
                  />
                </div>
                <NavPills color="primary" alignCenter queryKey="tab" tabs={tabs} />
              </GridItem>
            </GridContainer>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
